/**
 * FSD Part 15 — Revenue Management: hospitality rate/RevPAR management
 * (`internal/rvm`), NOT Sales revenue (FSD Part 5, `p05-sales.ts`). C17: one
 * section per FSD feature specification (15.2.1 RVM-F01, 15.2.2 RVM-F02) plus
 * the part catalogue (15.1). Every item is gated by an `rvm.*` permission, so
 * the whole part disappears for a tenant whose plan does not entitle it.
 *
 * Item ids are stable: favourites and recents (localStorage) key off them, so
 * an item may move between sections but its id never changes.
 */
import type { NavGroup } from '../types'
import { RVM_PERMISSIONS } from '@/modules/rvm/permissions'

export const P15_REVENUE_MANAGEMENT_GROUPS: NavGroup[] = [
  {
    id: 's-15.2.1',
    kind: 'feature',
    fsd: '15.2.1',
    code: 'RVM-F01',
    labelKey: 'nav.fsd.s15_2_1.short',
    fullTitleKey: 'nav.fsd.s15_2_1.full',
    items: [
      {
        id: 'rvm-on-the-books',
        labelKey: 'rvm.routeTitles.onTheBooks',
        to: '/rvm/on-the-books',
        icon: 'calendar',
        permission: RVM_PERMISSIONS.snapshotRead,
        keywordsKey: 'nav.items.rvmOnTheBooks.keywords',
        descriptionKey: 'nav.items.rvmOnTheBooks.description',
      },
      {
        id: 'rvm-pace',
        labelKey: 'rvm.routeTitles.pace',
        to: '/rvm/pace',
        icon: 'history',
        permission: RVM_PERMISSIONS.paceRead,
        keywordsKey: 'nav.items.rvmPace.keywords',
        descriptionKey: 'nav.items.rvmPace.description',
      },
      {
        id: 'rvm-commercial-report',
        labelKey: 'rvm.routeTitles.commercial',
        to: '/rvm/commercial-report',
        icon: 'dashboard',
        permission: RVM_PERMISSIONS.paceRead,
        keywordsKey: 'nav.items.rvmCommercial.keywords',
        descriptionKey: 'nav.items.rvmCommercial.description',
      },
      {
        id: 'rvm-forecasts',
        labelKey: 'rvm.routeTitles.forecasts',
        to: '/rvm/forecasts',
        icon: 'sitemap',
        permission: RVM_PERMISSIONS.forecastRead,
        keywordsKey: 'nav.items.rvmForecasts.keywords',
        descriptionKey: 'nav.items.rvmForecasts.description',
      },
      {
        id: 'rvm-accuracy',
        labelKey: 'rvm.routeTitles.accuracy',
        to: '/rvm/accuracy',
        icon: 'scale',
        permission: RVM_PERMISSIONS.accuracyRead,
        keywordsKey: 'nav.items.rvmAccuracy.keywords',
        descriptionKey: 'nav.items.rvmAccuracy.description',
      },
      // C13 §B1: the two configuration screens, last because they are set up
      // once and revisited by season.
      {
        id: 'rvm-dimensions',
        labelKey: 'rvm.routeTitles.dimensions',
        to: '/rvm/dimensions',
        icon: 'sitemap',
        permission: RVM_PERMISSIONS.dimensionRead,
        keywordsKey: 'nav.items.rvmDimensions.keywords',
        descriptionKey: 'nav.items.rvmDimensions.description',
      },
      {
        id: 'rvm-settings',
        labelKey: 'rvm.routeTitles.settings',
        to: '/rvm/settings',
        icon: 'settings',
        permission: RVM_PERMISSIONS.settingsRead,
        keywordsKey: 'nav.items.rvmSettings.keywords',
        descriptionKey: 'nav.items.rvmSettings.description',
      },
    ],
  },
  {
    id: 's-15.2.2',
    kind: 'feature',
    fsd: '15.2.2',
    code: 'RVM-F02',
    labelKey: 'nav.fsd.s15_2_2.short',
    fullTitleKey: 'nav.fsd.s15_2_2.full',
    items: [
      {
        id: 'rvm-displacements',
        labelKey: 'rvm.routeTitles.displacements',
        to: '/rvm/displacements',
        icon: 'users',
        permission: RVM_PERMISSIONS.displacementRead,
        keywordsKey: 'nav.items.rvmDisplacements.keywords',
        descriptionKey: 'nav.items.rvmDisplacements.description',
      },
      {
        id: 'rvm-hurdles',
        labelKey: 'rvm.routeTitles.hurdles',
        to: '/rvm/hurdles',
        icon: 'shield',
        permission: RVM_PERMISSIONS.hurdleRead,
        keywordsKey: 'nav.items.rvmHurdles.keywords',
        descriptionKey: 'nav.items.rvmHurdles.description',
      },
    ],
  },
  {
    id: 's-15.1',
    kind: 'catalogue',
    fsd: '15.1',
    labelKey: 'nav.fsd.s15_1.short',
    fullTitleKey: 'nav.fsd.s15_1.full',
    items: [
      {
        id: 'rvm-competitor-rates',
        labelKey: 'rvm.routeTitles.competitorRates',
        to: '/rvm/competitor-rates',
        icon: 'search',
        permission: RVM_PERMISSIONS.competitorRead,
        keywordsKey: 'nav.items.rvmCompetitorRates.keywords',
        descriptionKey: 'nav.items.rvmCompetitorRates.description',
        code: 'RVM-FR-018',
      },
    ],
  },
]
